import { SlopedRoofConfigurationState } from "@iko-design-center/shared";
import React, { ReactNode, useEffect } from "react";
import { useHistory, useLocation } from "react-router";
import { useApplication } from "../../../store/application/hooks";
import { ApplicationTypes } from "../../../store/application/store";
import { Home } from "../Home";
import { useSlopedRoof } from "../../../store/sloped-roof/hooks";
import { useRoutes } from "../../../store/routes/hooks";
import { useQueryParams } from "../../../store/query/sloped-roof.hooks";

export const Guard = (props: { children: ReactNode }) => {
  return (
    <>
      <ResetStateGuard>
        <ValidStateGuard>{props.children}</ValidStateGuard>
      </ResetStateGuard>
    </>
  );
};

// This makes sure, that when a user navigates backwards,
// either via the Navbar or via the browser back button,
// the state is reset. This so the user can reselect any options.
const ResetStateGuard = (props: { children: ReactNode }) => {
  const {
    resetConfiguration,
    resetConfigurationToRoofType,
    resetConfigurationToRoofInsulation,
  } = useSlopedRoof();
  const { Routes } = useRoutes() as any;
  const location = useLocation();
  const routeHome = Routes.HOME;
  const routeSlopedRoofRoofType = Routes.SLOPED_ROOF_1_ROOF_TYPE;
  const routeSlopedRoofRoofInsulation = Routes.SLOPED_ROOF_2_ROOF_INSULATION;
  const routeSlopedRoofOverview = Routes.SLOPED_ROOF_3_OVERVIEW;

  useEffect(() => {
    switch (location.pathname) {
      case routeHome:
        resetConfiguration();
        break;
      case routeSlopedRoofRoofType:
        resetConfigurationToRoofType();
        break;
      case routeSlopedRoofRoofInsulation:
        resetConfigurationToRoofInsulation();
        break;
      case routeSlopedRoofOverview:
        break;
    }
  }, [
    resetConfiguration,
    resetConfigurationToRoofType,
    resetConfigurationToRoofInsulation,
    location.pathname,
    routeHome,
    routeSlopedRoofRoofType,
    routeSlopedRoofRoofInsulation,
    routeSlopedRoofOverview,
  ]);

  return <>{props.children}</>;
};

// When a user refreshes the screen on let's say the OverviewPage,
// No state will be present in Redux, thus 'cannot read of undefined's will pop up.
// This prevents that.
const ValidStateGuard = (props: { children: ReactNode }) => {
  const { directLink, setDirectLink, setApplicationType, applicationType } = useApplication();
  const location = useLocation();
  const { redirectBasedOnQueryParams } = useQueryParams()
  const { configurationState } = useSlopedRoof();
  const { Routes } = useRoutes() as any;
  const history = useHistory();

  const valid = checkValidStateForRoute(
    applicationType,
    configurationState,
    location.pathname,
    Routes
  );

  useEffect(() => {
    if (!valid) {
      if (!directLink) {
        setDirectLink(true)
        setApplicationType(ApplicationTypes.SLOPED_ROOF);
        redirectBasedOnQueryParams()
      } else {
        history.push(Routes.HOME);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valid, setApplicationType, history, setDirectLink, directLink])

  return valid ? <>{props.children}</> : <Home />;
};

function checkValidStateForRoute(
  applicationType: ApplicationTypes | null,
  configurationState: SlopedRoofConfigurationState,
  route: string,
  Routes: any
): boolean | undefined {
  const { roofType, roofInsulation } = configurationState;

  const applicationTypeValid = applicationType !== null;
  const roofTypeValid = roofType.type !== null;
  const roofInsulationValid = roofInsulation.type !== null;

  switch (route) {
    case Routes.SLOPED_ROOF_3_OVERVIEW:
      return roofTypeValid && roofInsulationValid;
    case Routes.SLOPED_ROOF_2_ROOF_INSULATION:
      return roofTypeValid;
    case Routes.SLOPED_ROOF_1_ROOF_TYPE:
      return applicationTypeValid;
    case Routes.HOME:
      return true;
  }
}
